import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { GripHorizontal, Sparkles, X } from "lucide-react";
import AiAssistant from "./AiAssistant";
import { useMotion } from "./motion";

export default function AssistantWidget() {
    const [open, setOpen] = useState(false);
    const { reduced } = useMotion();
    const dragControls = useDragControls();
    const boundsRef = useRef(null);

    useEffect(() => {
        const openIt = () => setOpen(true);
        window.addEventListener("mdh:open-assistant", openIt);
        return () => window.removeEventListener("mdh:open-assistant", openIt);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    return (
        <>
            <div
                ref={boundsRef}
                className="pointer-events-none fixed inset-0 z-40"
                aria-hidden="true"
            />
            <AnimatePresence>
                {open && (
                    <motion.div
                        drag
                        dragListener={false}
                        dragControls={dragControls}
                        dragMomentum={false}
                        dragConstraints={boundsRef}
                        dragElastic={0}
                        initial={reduced ? false : { opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.96 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        data-testid="assistant-widget-panel"
                        className="fixed bottom-24 right-5 z-50 flex h-[560px] w-[calc(100vw-40px)] min-w-[320px] max-w-[calc(100vw-40px)] resize flex-col overflow-hidden rounded-xl border border-slate-800 bg-[#0D1420]/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] backdrop-blur-xl sm:min-h-[440px] sm:w-[28rem] sm:max-h-[calc(100vh-120px)]"
                    >
                        <div
                            data-testid="assistant-widget-drag-handle"
                            onPointerDown={(e) => dragControls.start(e)}
                            className="flex shrink-0 cursor-move touch-none items-center justify-between border-b border-slate-800/80 px-4 py-2.5"
                        >
                            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                <GripHorizontal className="h-3.5 w-3.5 text-slate-600" />
                                MDH AI Assistant
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="hidden font-mono text-[9px] text-slate-600 sm:inline">
                                    drag to move · corner to resize
                                </span>
                                <button
                                    data-testid="assistant-widget-close"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={() => setOpen(false)}
                                    className="rounded p-1 text-slate-500 transition-colors hover:text-slate-300"
                                    aria-label="Close assistant"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </span>
                        </div>
                        <div className="min-h-0 flex-1">
                            <AiAssistant fill />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            <button
                data-testid="assistant-widget-button"
                onClick={() => setOpen((v) => !v)}
                className="fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full border border-cyan-500/40 bg-cyan-950/80 text-cyan-300 shadow-[0_0_24px_rgba(6,182,212,0.35)] backdrop-blur-md transition-all hover:bg-cyan-900 hover:shadow-[0_0_32px_rgba(6,182,212,0.5)]"
                aria-label="Open AI assistant"
            >
                <Sparkles className="h-5 w-5" />
            </button>
        </>
    );
}
